import { InvestmentApiItem, InvestmentChoice } from './investment-choice.model';

// Target Date Retirement Portfolios — manually maintained
export const TARGET_DATE_PORTFOLIOS: InvestmentChoice[] = [
  ...[2080, 2075, 2070, 2065, 2060, 2055, 2050, 2045, 2040, 2035, 2030, 2025, 2020].map(year => ({
    category: 'Target Date Retirement',
    name: `Catholic Values Target Retirement Model Portfolio ${year}`,
    description: `Target Retirement ${year} — Designed for investors who plan to retire around the year ${year}. The fund automatically adjusts its investment mix over time to balance growth potential and risk reduction as the target date approaches.`,
    svgIcon: '',
    blendedExpenseRatio: 0,
    symbol: `td${year}`,
    color: '#002236',
  })),
  {
    category: 'Target Date Retirement',
    name: 'Catholic Values Retirement Income Model Portfolio',
    description: 'Retirement Income — Designed for individuals who are already in retirement or are about to retire.',
    svgIcon: '',
    blendedExpenseRatio: 0,
    symbol: 'td1933',
    color: '#002236',
  },
  {
    id: 7846,
    category: 'Target Date Retirement',
    name: 'IFC Risk Based Index Portfolio 80',
    description: '',
    svgIcon: '',
    blendedExpenseRatio: 0,
    symbol: 'port80srwf_ifa',
    color: '#002236',
  },
  {
    id: 7866,
    category: 'Target Date Retirement',
    name: 'IFC Risk Based Index Portfolio 60',
    description: '',
    svgIcon: '',
    blendedExpenseRatio: 0,
    symbol: 'port60srwf_ifa',
    color: '#002236',
  },
  {
    id: 7886,
    category: 'Target Date Retirement',
    name: 'IFC Risk Based Index Portfolio 40',
    description: '',
    svgIcon: '',
    blendedExpenseRatio: 0,
    symbol: 'port40srwf_ifa',
    color: '#002236',
  },
];

// Target Date Portfolio Benchmarks — mirrors TARGET_DATE_PORTFOLIOS
export const TARGET_DATE_PORTFOLIO_BENCHMARKS: InvestmentChoice[] = [
  ...[2080, 2075, 2070, 2065, 2060, 2055, 2050, 2045, 2040, 2035, 2030, 2025, 2020].map(year => ({
    category: 'Target Date Benchmarks',
    name: `Blended Prospectus Benchmark Portfolio ${year}`,
    description: `Blended Prospectus Benchmark for Target Retirement ${year}`,
    svgIcon: '',
    blendedExpenseRatio: 0,
    symbol: `bm${year}`,
    color: '#546e7a',
  })),
  {
    category: 'Target Date Benchmarks',
    name: 'Blended Prospectus Benchmark Retirement Income',
    description: 'Blended Prospectus Benchmark for Retirement Income',
    svgIcon: '',
    blendedExpenseRatio: 0,
    symbol: 'bm1933',
    color: '#546e7a',
  },
  {
    id: 7947,
    category: 'Target Date Benchmarks',
    name: 'Benchmark Risk Based Index Portfolio 80',
    description: 'Blended Prospectus Benchmark for IFC Risk Based Index Portfolio 80. Comprising of Russell 3000 Index, MSCI World ex USA Index, S&P Global REIT Index, MSCI EM, and BBgBarc US Agg Bond. Performance is shown gross of IFC Advisor Fees.',
    svgIcon: '',
    blendedExpenseRatio: 0,
    symbol: 'port80srwfbm_ifa',
    color: '#546e7a',
  },
  {
    id: 7967,
    category: 'Target Date Benchmarks',
    name: 'Benchmark Risk Based Index Portfolio 60',
    description: 'Blended Prospectus Benchmark for IFC Risk Based Index Portfolio 60. Comprising of Russell 3000 Index, MSCI World ex USA Index, S&P Global REIT Index, MSCI EM, and BBgBarc US Agg Bond. Performance is shown gross of IFC Advisor Fees.',
    svgIcon: '',
    blendedExpenseRatio: 0,
    symbol: 'port60srwfbm_ifa',
    color: '#546e7a',
  },
  {
    id: 7987,
    category: 'Target Date Benchmarks',
    name: 'Benchmark Risk Based Index Portfolio 40',
    description: 'Blended Prospectus Benchmark for IFC Risk Based Index Portfolio 40. Comprising of Russell 3000 Index, MSCI World ex USA Index, S&P Global REIT Index, MSCI EM, and BBgBarc US Agg Bond. Performance is shown gross of IFC Advisor Fees.',
    svgIcon: '',
    blendedExpenseRatio: 0,
    symbol: 'port40srwfbm_ifa',
    color: '#546e7a',
  },
];

/** Maps a raw API item to an InvestmentChoice, with trimmed hex color. */
export function apiItemToChoice(item: InvestmentApiItem): InvestmentChoice {
  const hex = item.color?.trim() ?? '';
  return {
    id: item.id,
    category: (item.id >= 14110 && item.id <= 14210) ? 'IFC Index Portfolios' : 'IFC Indexes',
    name: item.name.replace('IFA ', 'IFC '),  // remove "IFC " prefix from names
    description: item.description ?? '',
    svgIcon: '',
    blendedExpenseRatio: 0,
    symbol: item.symbol,
    color: hex ? `#${hex}` : '',
  };
}

/**
 * Maps a raw API portfolio item to a Glide Path InvestmentChoice.
 * Symbols are suffixed with `_gp` to keep them unique; id is omitted so URL
 * serialisation falls back to the symbol rather than the numeric id.
 */
export function apiItemToGlidePathChoice(item: InvestmentApiItem): InvestmentChoice {
  const hex = item.color?.trim() ?? '';
  return {
    // id intentionally omitted — URL persistence uses symbol instead of numeric id
    category: 'IFC Index Portfolios (Glide Path)',
    name: item.name.replace('IFA ', 'IFC ') + ' (GP)',
    description: item.description ?? '',
    svgIcon: '',
    blendedExpenseRatio: 0,
    symbol: item.symbol + '_gp',
    color: hex ? `#${hex}` : '',
  };
}

/** API URL for investment choices (portfolios 14110–14210 + IFC index IDs + Risk Based IDs). */
export const INVESTMENTS_API_URL =
  `https://data2.ifa.com/odata/v1/investment/?$select=name,symbol,id,color,description` +
  `&$filter=id in (884,534,888,6970,24943,7846,7866,7886,7947,7967,7987) or (id ge 14110 and id le 14210)`;

// ── Dimensional Index Portfolios (gross of IFC Advisory Fees) ─────────────
export const DIMENSIONAL_INDEX_PORTFOLIOS: InvestmentChoice[] = (
  Array.from({ length: 21 }, (_, k) => k * 5)   // 0, 5, 10, … 100
).map(equity => ({
  category: 'Dimensional Index Portfolios',
  name: `Dimensional Index Portfolio ${equity}`,
  description: '',
  svgIcon: '',
  blendedExpenseRatio: 0,
  symbol: `dfa_idp_sr_${equity}`,
  color: '#009933',
}));

// ── Dimensional Benchmark Portfolios (gross of IFC Advisory Fees) ─────────
export const DIMENSIONAL_BENCHMARK_PORTFOLIOS: InvestmentChoice[] = (
  Array.from({ length: 21 }, (_, k) => k * 5)
).map(equity => ({
  category: 'Dimensional Prospectus Benchmark Portfolios',
  name: `Dimensional Prospectus Benchmark Portfolio ${equity}`,
  description: '',
  svgIcon: '',
  blendedExpenseRatio: 0,
  symbol: `port${equity}srwfbm_ifa`,
  color: '#990000',
}));

// ── Dimensional Mutual Fund Portfolios (gross of IFC Advisory Fees) ───────
export const DIMENSIONAL_MUTUAL_FUND_PORTFOLIOS: InvestmentChoice[] = (
  Array.from({ length: 21 }, (_, k) => k * 5)   // 0, 5, 10, … 100
).map(equity => ({
  category: 'Dimensional Mutual Fund Portfolios',
  name: `Dimensional Mutual Fund Portfolio ${equity}`,
  description: '',
  svgIcon: '',
  blendedExpenseRatio: 0,
  symbol: `dfamf${equity}`,
  color: '#7B3F9E',
}));
